import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DeferredPromise } from '../../models/deferred-promise';
import { Endpoint, UpdateStatusType, MessageType } from '../../models/enums';
import { CommonService } from '../common/common.service';
import { HttpService } from '../http/http.service';
import { v4 as uuidv4 } from 'uuid';
import { LoginData } from '../../models/login-data';

@Injectable()
export class RestService {

    constructor(
        private httpService: HttpService,
        private commonService: CommonService
    ) { }

    postWithObservable(endpoint: Endpoint, data?: unknown, showErrorModal = true): Observable<any> {
        return this.httpService.postWithObservable(endpoint, data ? data : {}, showErrorModal);
    }

    post(endpoint: Endpoint, data?: unknown, showErrorModal = true, messageParams: boolean | [MessageType, UpdateStatusType] = false, customAuthHeader?: string): Promise<any> {
        return this.httpService.post(endpoint, data ? data : {}, showErrorModal, messageParams, customAuthHeader);
    }

    getSplashMessage(): Observable<unknown> {
        return this.httpService.get(this.commonService.nonRestUrl + '/ediscovery/getSplashMessage');
    }

    getUserAndCheckLicense(loginInfo: LoginData, currentUserDeviceId: any, request: DeferredPromise<unknown>, sessionToken: string): void {
        // Step 2: Get user
        this.post(Endpoint.userManager_getCurrentUser, {}, false).then(getCurrentUserResponse => {
            if (getCurrentUserResponse?.user) {
                this.commonService.currentUser = getCurrentUserResponse.user;
                if (this.commonService.isTPAuthEnabledSystem) {
                    this.commonService.currentUser.name = this.commonService.currentUser.userName = getCurrentUserResponse.user.email;
                }
                this.commonService.currentUser.originalCustomerName = this.commonService.currentUser.customerName;
                this.commonService.currentUser.loginUsername = loginInfo?.username ?? this.commonService.currentUser.name;
                this.commonService.currentUser.deviceId = currentUserDeviceId;
                this.commonService.contextHandle = this.commonService.currentUser.customerName;
                this.commonService.policyAcknowledgment = this.commonService.currentUser.ediscoveryEula;
                // Step 3: Check license
                this.post(Endpoint.realmManager_getLicensedFeatures, {}, false).then(() => {
                    request.resolve(sessionToken);
                }, error => request.reject(error));
            } else {
                request.reject('Cross-Origin Read Blocking (CORB) blocked cross-origin response');
            }
        }, error => request.reject(error));
    }

    login(loginInfo: LoginData, sessionToken: string): Promise<any> {
        // Step 0: Set mock mode
        if (loginInfo?.useMockServer) {
            this.commonService.setMockMode();
        }
        this.commonService.sessionToken = sessionToken ? sessionToken : 'bogus:bogus';
        const request = new DeferredPromise<unknown>();
        const currentUserDeviceId = loginInfo?.userDeviceId ? loginInfo.userDeviceId : uuidv4();
        // const currentUserDeviceId = uuidv4();

        if (sessionToken?.length !== 0) {
            this.getUserAndCheckLicense(loginInfo, currentUserDeviceId, request, sessionToken);
        } else {
            this.commonService.sessionToken = loginInfo.bearerToken ? (loginInfo.bearerToken.startsWith('Bearer') ? loginInfo.bearerToken : 'Bearer ' + loginInfo.bearerToken) : loginInfo.username + ':' + loginInfo.password;
            const createSessionRequestData = (loginInfo.isRememberMeOn && loginInfo.bearerToken === null) ?
                {
                    deviceID: loginInfo.mfaDeviceId,
                    userDeviceID: currentUserDeviceId,
                    rememberMe: true
                } : {
                    deviceID: loginInfo.mfaDeviceId,
                    userDeviceID: currentUserDeviceId
                };
            // Step 1: Create session
            this.post(Endpoint.realmManager_createSession, createSessionRequestData, false).then(createSessionResponse => {
                if (createSessionResponse) {
                    if (createSessionResponse.errorMessage && createSessionResponse.errorMessage.includes('Your IP address does not match')) {
                        return request.reject(createSessionResponse.errorMessage);
                    } else {
                        this.getUserAndCheckLicense(loginInfo, currentUserDeviceId, request, createSessionResponse);
                    }
                }
            }, error => request.reject(error));
        }
        return request.promise;
    }

    getPasswordPolicy(userName: string, oldPassword: string, forceAuthHeader = false, isSystemSettingsContext = false): Promise<any> {
        if (!this.commonService.sessionToken || !this.commonService.sessionToken.length || forceAuthHeader) {
            return this.post(Endpoint.realmManager_getPasswordPolicy, { systemScope: isSystemSettingsContext }, false, false, userName + ':' + oldPassword);
        } else {
            return this.post(Endpoint.realmManager_getPasswordPolicy, { systemScope: isSystemSettingsContext }, false);
        }
    }
}
